// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import isProd from '@utils/isProd';

import { ValueType } from 'components/DebounceSelect';
import { searchCarriers } from 'lib/api/searchCarriers';
import { searchCustomers } from 'lib/api/searchCustomers';
import { searchLocations } from 'lib/api/searchLocations';
import {
  CompanyCoreInfo,
  TMSCarrier,
  TMSCommodity,
  TMSCustomer,
  TMSLocation,
} from 'types/Load';
import { TransportType } from 'types/QuoteRequest';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';

/**
 * Optionally disable required fields in development mode
 */
export const devDisableRequiredFields = !isProd() && false;

/**
 * Context from the sidebar used when building a load from an AI suggestion.
 * Ensures Builder metrics can tie built loads to suggestions.
 */
export type LoadBuildingSuggestionContext = {
  /** The suggestion the user clicked to prefill the form; positive ids are sent to create-load. */
  clickedSuggestion: MaybeUndef<{ id: number }>;
  // Thread ID of the current email (sent when building without suggestion).
  // Never empty when on email platform
  threadId: string;

  // Email external ID when Drumkit id is not available (sent when building without suggestion).
  // May be empty when on non-email platform
  emailExternalId: string;

  // Drumkit email ID when available (sent when building without suggestion).
  // May be 0 if email not processed and saved to DB yet
  emailId: number;

  /**
   * Quick Quote request id from QQ search (ENG-7582). Sent so MG can inject Quote Number
   * without a load-building AI suggestion. Prefer positive ids only.
   */
  quoteRequestId?: number;
};

/** Suggestion / email metadata attached to create-load requests for Builder metrics. */
export type LoadBuildingSuggestionMetadata = {
  suggestionId?: number;
  quoteRequestId?: number;
  threadId?: string;
  emailId?: number;
  emailExternalId?: string;
};

/**
 * Merges load-building suggestion context into a create-load request so the API
 * receives suggestionId, threadId, and emailId. All TMS load forms must use this
 * when building the payload in create mode so Builder metrics work correctly.
 *
 * @param context - Sidebar context: clickedSuggestion, threadId, emailId/emailExternalId from SidebarStateContext
 * @param partial - The create-load request built by the TMS form (load payload, metadata, etc.)
 * @returns A complete CreateLoadRequest with suggestionId, threadId, and emailId or emailExternalId set from context
 *
 * @example
 * const reqData = withLoadBuildingSuggestionContext(
 *   { clickedSuggestion, threadId, emailId, emailExternalId: threadItemId },
 *   { load: { ...data, tmsID }, metadata }
 * );
 * const res = await createLoad(reqData);
 */
/**
 * Real load-building suggestions use positive DB ids. Synthetic SharePoint rows use
 * negative ids (see `buildSyntheticSharepointLoadBuildingSuggestion`) — the API expects
 * `SuggestionID` as uint and rejects negatives, so we omit those.
 */
function suggestionIdForCreateLoadApi(
  clickedSuggestion: LoadBuildingSuggestionContext['clickedSuggestion']
): Undef<number> {
  const id = clickedSuggestion?.id;
  if (id == null || !Number.isFinite(id) || id <= 0) {
    return undefined;
  }
  return id;
}

function quoteRequestIdForCreateLoadApi(
  quoteRequestId: LoadBuildingSuggestionContext['quoteRequestId']
): Undef<number> {
  if (
    quoteRequestId == null ||
    !Number.isFinite(quoteRequestId) ||
    quoteRequestId <= 0
  ) {
    return undefined;
  }
  return quoteRequestId;
}

export function withLoadBuildingSuggestionContext<T extends object>(
  context: LoadBuildingSuggestionContext,
  partial: T
): T & LoadBuildingSuggestionMetadata {
  const request: T & LoadBuildingSuggestionMetadata = {
    ...partial,
    suggestionId: suggestionIdForCreateLoadApi(context.clickedSuggestion),
    quoteRequestId: quoteRequestIdForCreateLoadApi(context.quoteRequestId),
    threadId: context.threadId,
    emailId: context.emailId,
    emailExternalId: context.emailExternalId,
  };
  return request;
}

export type GenericCompanySearchableFields = keyof Pick<
  CompanyCoreInfo,
  'name' | 'nameAddress' | 'addressLine1' | 'city' | 'externalTMSID'
>;

export type CarrierSearchableFields =
  GenericCompanySearchableFields | 'dotNumber';

export const locationSearchHandler = async ({
  field,
  value,
  tmsID,
  setLocations,
  locations,
  customerID,
  customerExternalTmsId,
}: {
  tmsID: number;
  locations: Maybe<TMSLocation[]>;
  setLocations: React.Dispatch<React.SetStateAction<Maybe<TMSLocation[]>>>;
  field: GenericCompanySearchableFields;
  value: string;
  customerID?: number;
  customerExternalTmsId?: string;
}) => {
  // require at least 3 characters to search
  if (value.length > 2) {
    const searchRes = await searchLocations(
      tmsID,
      field,
      value,
      undefined,
      undefined,
      undefined,
      undefined,
      customerID,
      customerExternalTmsId
    );

    if (searchRes.isOk()) {
      const { locationList: searchedLocations } = searchRes.value;
      setLocations(searchedLocations);

      return searchedLocations && searchedLocations.length
        ? mapLocationsToSelectOptions(searchedLocations)
        : [];
    }
  }

  // Frontend filtering should search original/seeded locations for matches,
  // and update the filteredLocations state to keep them consistent
  const filteredResults = (locations ?? []).filter((loc) => {
    const searchableText = `${loc.name} ${loc.addressLine1}`.toLowerCase();
    return searchableText.includes(value.toLowerCase());
  });

  setLocations(filteredResults);
  return mapLocationsToSelectOptions(filteredResults);
};

export const customerSearchHandler = async ({
  field,
  value,
  tmsID,
  setCustomers,
  customers,
  filterCustomers,
}: {
  tmsID: number;
  customers: Maybe<TMSCustomer[]>;
  setCustomers: React.Dispatch<React.SetStateAction<Maybe<TMSCustomer[]>>>;
  field: GenericCompanySearchableFields;
  value: string;
  filterCustomers?: (customers: TMSCustomer[]) => TMSCustomer[];
}) => {
  const applyFilter = (list: TMSCustomer[]) =>
    filterCustomers ? filterCustomers(list) : list;

  // require at least 2 characters for text search; allow any length for externalTMSID lookup
  const canSearch =
    value.length > 1 || (field === 'externalTMSID' && value.length >= 1);
  if (canSearch) {
    const searchRes = await searchCustomers(tmsID, field, value);

    if (searchRes.isOk()) {
      const { customerList: searchedCustomers } = searchRes.value;

      const filteredCustomers = applyFilter(searchedCustomers ?? []);
      setCustomers(filteredCustomers);

      return filteredCustomers.length
        ? mapCustomerToSelectOptions(filteredCustomers)
        : [];
    }
  }

  // Frontend filtering should search original/seeded customers for matches,
  // and update the filteredCustomers state to keep them consistent
  const matched = (customers ?? []).filter((customer) => {
    const searchableText =
      `${customer.name} ${customer.addressLine1}`.toLowerCase();
    return searchableText.includes(value.toLowerCase());
  });
  const filteredResults = applyFilter(matched);
  setCustomers(filteredResults);
  return mapCustomerToSelectOptions(filteredResults);
};

export const carrierSearchHandler = async ({
  field,
  value,
  tmsID,
  setCarriers,
  carriers,
}: {
  tmsID: number;
  carriers: Maybe<TMSCarrier[]>;
  setCarriers: React.Dispatch<React.SetStateAction<Maybe<TMSCarrier[]>>>;
  field: CarrierSearchableFields;
  value: string;
}) => {
  // require at least 3 characters to search
  if (value.length > 2) {
    const searchRes = await searchCarriers(tmsID, field, value);

    if (searchRes.isOk()) {
      const { carrierList: searchedCarriers } = searchRes.value;
      setCarriers(searchedCarriers);

      return searchedCarriers && searchedCarriers.length
        ? mapCarriersToSelectOptions(searchedCarriers)
        : [];
    }
  }

  /**
   * Searches that are less than 3 characters dont provide enough context to search amongst
   * all carriers. In that case, apply that filter to the entries that are already loaded.
   */
  return mapCarriersToSelectOptions(carriers ?? []).filter((c) =>
    c.label.toLocaleLowerCase().includes(value.toLocaleLowerCase())
  );
};

export const searchParamOptions: {
  key: keyof CompanyCoreInfo;
  label: string;
}[] = [
  { key: 'name', label: 'Name' },
  { key: 'addressLine1', label: 'Street' },
];

export const mapLocationsToSelectOptions = (
  locations: Maybe<TMSLocation[]>,
  labelIsName?: boolean
): ValueType[] => {
  if (!locations) return [];

  return locations.map((option: TMSLocation) => {
    // Create address + city + state display format
    const addressParts = [];
    if (option.addressLine1) addressParts.push(option.addressLine1);
    if (option.city) addressParts.push(option.city);
    if (option.state) addressParts.push(option.state);

    const addressString = addressParts.join(', ');
    const displayName =
      addressString && addressString.length > 35
        ? `${addressString.substring(0, 35)}...`
        : addressString;

    return {
      value: option.externalTMSID,
      // Show address + city + state format for better UX
      label:
        labelIsName || option.name
          ? displayName || option.name || option.externalTMSID
          : option.externalTMSID,
      // Store the full object for easier access in components
      ...option,
    };
  });
};

export const mapCustomerToSelectOptions = (
  customers: Maybe<TMSCustomer[]>
): ValueType[] => {
  if (!customers) return [];

  return customers.map((option: TMSCustomer) => ({
    value: option.externalTMSID,
    label: `${option.name}`,
    // Spread full customer object so DebounceSelect can access externalTMSID and other fields
    ...option,
  }));
};

export const mapCarriersToSelectOptions = (
  carriers: Maybe<TMSCarrier[]>
): ValueType[] => {
  if (!carriers) return [];

  return carriers.map((option: TMSCarrier) => ({
    value: option.externalTMSID,
    name: option.name,
    addressLine1: option.addressLine1,
    addressLine2: option.addressLine2,
    city: option.city,
    state: option.state,
    label: option.name,
  }));
};

export const mapCommoditiesToAntdOptions = (
  commodities: Maybe<TMSCommodity[]>
): ValueType[] => {
  if (!commodities) return [];

  return commodities.map((option: TMSCommodity) => {
    // Priority order for display name:
    // 1. commodityDescription (most descriptive)
    // 2. commodity (fallback)
    // 3. externalTMSID (last resort)
    const displayName =
      option.commodityDescription ||
      option.commodity ||
      option.externalTMSID ||
      '';

    return {
      value: option.externalTMSID,
      label: displayName,
      data: option, // Store full object for reference
    };
  });
};

export const commoditySearchHandler = async ({
  field: _field,
  value,
  commodities,
}: {
  commodities: Maybe<TMSCommodity[]>;
  field: GenericCompanySearchableFields;
  value: string;
}): Promise<ValueType[]> => {
  if (!value || value.length < 2) {
    return mapCommoditiesToAntdOptions(commodities ?? []);
  }

  // Filter commodities by search term
  const filtered = (commodities ?? []).filter((commodity) => {
    const searchTerm = value.toLowerCase();
    const description = (commodity.commodityDescription || '').toLowerCase();
    const commodityName = (commodity.commodity || '').toLowerCase();
    const nmfc = (commodity.nmfc || commodity.nmfcNumber || '').toLowerCase();

    return (
      description.includes(searchTerm) ||
      commodityName.includes(searchTerm) ||
      nmfc.includes(searchTerm)
    );
  });

  return mapCommoditiesToAntdOptions(filtered);
};

type WithExternalTMSID = {
  externalTMSID: string;
};

type WithOptionalAddress = {
  addressLine1?: string;
};

function hasPopulatedAddressLine1(record: WithOptionalAddress): boolean {
  return Boolean(record.addressLine1?.trim());
}

/**
 * Prefer a TMS row with address data over an ID-only stub.
 * When richness is equal, preferCandidateOnTie lets picker payloads beat list rows.
 */
export function shouldUpgradeTMSRecord<T extends WithOptionalAddress>(
  candidate: T,
  incumbent: T
): boolean {
  return shouldPreferTMSRecord(candidate, incumbent, true);
}

function shouldPreferTMSRecord<T extends WithOptionalAddress>(
  candidate: T,
  incumbent: T,
  preferCandidateOnTie: boolean
): boolean {
  const candidateHasAddress = hasPopulatedAddressLine1(candidate);
  const incumbentHasAddress = hasPopulatedAddressLine1(incumbent);
  if (candidateHasAddress && !incumbentHasAddress) {
    return true;
  }
  if (!candidateHasAddress && incumbentHasAddress) {
    return false;
  }
  return preferCandidateOnTie;
}

function coalesceTMSRecord<T extends WithExternalTMSID & WithOptionalAddress>(
  incumbent: T | undefined,
  candidate: T | undefined,
  preferCandidateOnTie: boolean
): T | undefined {
  if (!candidate) {
    return incumbent;
  }
  if (!incumbent) {
    return candidate;
  }
  return shouldPreferTMSRecord(candidate, incumbent, preferCandidateOnTie)
    ? candidate
    : incumbent;
}

export function normalizeExternalTMSID(id: unknown): string {
  return id != null ? String(id).trim() : '';
}

/**
 * Type guard: does the value have a non-empty externalTMSID?
 */
export function hasExternalTMSID<T extends WithExternalTMSID>(
  value: unknown
): value is T {
  return (
    typeof value === 'object' &&
    value !== null &&
    'externalTMSID' in value &&
    normalizeExternalTMSID((value as T).externalTMSID) !== ''
  );
}

/**
 * Resolve selected item from list, falling back to raw item or value object.
 * Used when the list prop may be stale but we have the full object from the picker.
 */
export function resolveSelectedItem<
  T extends WithExternalTMSID & WithOptionalAddress,
>(
  id: string,
  list: Maybe<T[]>,
  selectedRawItem?: unknown,
  value?: unknown
): T | undefined {
  const fromList = list?.find(
    (item) => normalizeExternalTMSID(item.externalTMSID) === id
  );

  let resolved = fromList;

  if (
    hasExternalTMSID<T>(selectedRawItem) &&
    normalizeExternalTMSID(selectedRawItem.externalTMSID) === id
  ) {
    resolved = coalesceTMSRecord(resolved, selectedRawItem, true);
  }

  if (
    hasExternalTMSID<T>(value) &&
    normalizeExternalTMSID(value.externalTMSID) === id
  ) {
    resolved = coalesceTMSRecord(resolved, value, true);
  }

  return resolved;
}

/**
 * Check if the selected object exists in the array, if not, inject the defaultObject
 */
export function injectSelectedObject<
  T extends WithExternalTMSID & WithOptionalAddress,
>(selected: T, array: T[]): T[] {
  const selectedId = normalizeExternalTMSID(selected.externalTMSID);
  if (!selectedId) {
    // Don't inject if externalTMSID is empty
    return array;
  }

  const existingIndex = array.findIndex(
    (obj) => normalizeExternalTMSID(obj.externalTMSID) === selectedId
  );

  if (existingIndex === -1) {
    return [selected, ...array];
  }

  const existing = array[existingIndex];
  if (shouldPreferTMSRecord(selected, existing, false)) {
    const next = [...array];
    next[existingIndex] = selected;
    return next;
  }

  return array;
}

// Helper function to resolve transport type string to TransportType enum
export const resolveTransportType = (
  typeString: Maybe<string>
): Maybe<TransportType> => {
  if (!typeString || !typeString.trim()) {
    return null; // Return null for empty/null/blank string
  }

  const upperCaseTypeString = typeString.trim().toUpperCase();

  if (
    Object.values(TransportType).includes(
      upperCaseTypeString as TransportType
    ) &&
    upperCaseTypeString !== TransportType.SPECIAL
  ) {
    return upperCaseTypeString as TransportType;
  }

  const normalizedInput = upperCaseTypeString
    .replace(/[-–—]/g, ' ') // Replace any kind of dash with space
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim();

  if (typeString.toLowerCase().includes('van')) {
    return TransportType.VAN;
  }
  if (normalizedInput === 'VAN DRY' || normalizedInput === 'DRY VAN')
    return TransportType.VAN;
  if (
    normalizedInput.includes('REEFER') ||
    normalizedInput.includes('REFRIGERATED') ||
    normalizedInput.includes('TEMP CONTROL')
  )
    return TransportType.REEFER;
  if (
    normalizedInput.includes('FLATBED') ||
    normalizedInput === 'FLAT BED' ||
    normalizedInput === 'FLAT'
  )
    return TransportType.FLATBED;
  if (
    normalizedInput.includes('BOXTRUCK') ||
    normalizedInput === 'BOX TRUCK' ||
    normalizedInput === 'BOX'
  )
    return TransportType.BOXTRUCK;
  if (normalizedInput.includes('HOTSHOT') || normalizedInput === 'HOT SHOT')
    return TransportType.HOTSHOT;
  if (normalizedInput.includes('SPRINTER')) return TransportType.SPRINTER;
  if (normalizedInput.includes('DRAYAGE')) return TransportType.DRAYAGE;
  if (normalizedInput === 'VAN') return TransportType.VAN;
  if (
    normalizedInput.includes('TRUCKLOAD') ||
    normalizedInput === 'TL' ||
    normalizedInput === 'TRUCK LOAD'
  )
    return TransportType.TRUCKLOAD;

  return null;
};

/**
 * Normalizes an object with numeric keys into an array.
 *
 * Sometimes, JS/TS parses arrays from API responses as Records/Objects with numbered keys
 * (e.g., `{ 0: 'a', 1: 'b', 2: 'c' }` instead of `['a', 'b', 'c']`).
 * This function normalizes such objects back into proper arrays while preserving type safety.
 *
 * @template T - The type of elements in the array
 * @param {Undef<T[] | Record<number | string, T>>} value - The value to normalize. Can be an array, an object with numeric keys, or undefined.
 * @returns {Undef<T[]>} - The normalized array, or undefined if the input was undefined.
 *
 * @example
 * // With string array
 * normalizeToArray(['a', 'b', 'c']) // ['a', 'b', 'c']
 *
 * @example
 * // With object that has numeric keys
 * normalizeToArray({ 0: 'a', 1: 'b', 2: 'c' }) // ['a', 'b', 'c']
 *
 * @example
 * // With complex types
 * normalizeToArray<Customer>({ 0: customer1, 1: customer2 }) // [customer1, customer2]
 *
 * @example
 * // With undefined
 * normalizeToArray(undefined) // undefined
 */
export const normalizeToArray = <T>(
  value: Undef<T[] | Record<number | string, T>>
): Undef<T[]> => {
  if (!value) return undefined;
  if (Array.isArray(value)) return value;
  return Object.values(value);
};

// Sometimes, JS/TS parses the array from API responses as a Record/Object with numbered keys,
// so for purposes of consistency with our refNumberCandidates type (and other types)
// We normalize to array
export const normalizeToStringArray = (
  candidates: Undef<string[] | Record<string, string>>
): Undef<string[]> => {
  if (!candidates) return undefined;
  if (Array.isArray(candidates)) return candidates;
  return Object.values(candidates) as string[];
};
